/**
 * Hộp "Phát khẩn cấp" mở từ nút Phát ngay của trang Bảng điện tử (theo mẫu tỉnh). Phát ngay = phát
 * khẩn cấp = DV011 (CV276); `DanhSachBanTin` của DV011 là danh sách nên được chọn nhiều bản tin.
 *
 *   - Trên: "Chọn vùng phát" (`PhanVungManHinh`: Toàn màn hình / Phân vùng màn hình) và "Loại bản tin"
 *     (Video / Hình ảnh / Âm thanh) — lọc danh sách bên trái.
 *   - Trái: bản tin trong Kho nội dung (đã duyệt / đang phát / đã phát), phân trang ở máy chủ; Xem lại
 *     và "+ Thêm".
 *   - Phải: bản tin đã chọn theo thứ tự phát, có Vùng phát của từng bản tin; "Phát khẩn cấp" gửi
 *     `broadcast/emergency` với `banTinIds`, "Xóa tất cả" bỏ chọn.
 *   - Đích: bảng đã tích ở danh sách; không tích thì mọi bảng trên địa bàn đang xem.
 */
import { useId, useState, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viBangTin } from '../ngon-ngu/vi-bang-tin';
import {
  banTinApi,
  LOC_THEO_NHOM_LOAI,
  TRANG_THAI_CHON_PHAT,
  type DongBanTin,
  type NhomLoaiBanTin,
} from '../api/ban-tin-api';
import {
  bangTinApi,
  type KetQuaGuiBT,
  type PhanVungManHinhBT,
  type TrangThaiThietBiBT,
} from '../api/bang-tin-api';
import type { TrangDuLieu } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { TrinhPhatAmThanh } from '../thanh-phan/TrinhPhatAmThanh';
import { kieuTep, loiThanhChuoi, TrinhXemTep } from './BangTinChung';

const t = viBangTin.khanCap;

/** Số bản tin mỗi trang của danh sách bên trái. */
const SO_DONG_TRANG = 15;
/** Tối đa một lần phát (máy chủ chặn cùng mức). */
const TOI_DA_CHON = 20;

const DS_LOAI: NhomLoaiBanTin[] = ['video', 'hinh-anh', 'am-thanh'];

export interface ApiPhatKhanCapBT {
  danhSachBanTin: typeof banTinApi.danhSach;
  doanAmThanh: typeof banTinApi.doanAmThanh.danhSach;
  linkTep: typeof banTinApi.linkTep;
  phatKhanCap: typeof bangTinApi.phatKhanCap;
}

const API_MAC_DINH: ApiPhatKhanCapBT = {
  danhSachBanTin: banTinApi.danhSach,
  doanAmThanh: banTinApi.doanAmThanh.danhSach,
  linkTep: banTinApi.linkTep,
  phatKhanCap: bangTinApi.phatKhanCap,
};

export function HopPhatKhanCapBangTin({
  bangChon,
  diaBan,
  tenDiaBan,
  onDong,
  onXong,
  api = API_MAC_DINH,
}: {
  /** Bảng đã tích ở danh sách; rỗng = mọi bảng trên `diaBan`. */
  bangChon: TrangThaiThietBiBT[];
  diaBan: string[];
  tenDiaBan: string;
  onDong: () => void;
  onXong: (kq: KetQuaGuiBT | null) => void;
  api?: ApiPhatKhanCapBT;
}): ReactNode {
  const idTieuDe = useId();
  const [phanVung, datPhanVung] = useState<PhanVungManHinhBT>('0');
  const [loai, datLoai] = useState<NhomLoaiBanTin>('video');
  const [trang, datTrang] = useState(1);
  const [chon, datChon] = useState<DongBanTin[]>([]);
  const [xem, datXem] = useState<{ id: string; tieuDe: string; kieu: string; url: string | null } | null>(
    null,
  );
  const [loi, datLoi] = useState<string | null>(null);
  const [dangGui, datDangGui] = useState(false);

  const ds = dungTaiDuLieu<TrangDuLieu<DongBanTin>>({
    tai: (signal) =>
      api.danhSachBanTin(
        {
          trangThai: TRANG_THAI_CHON_PHAT,
          loaiBanTin: LOC_THEO_NHOM_LOAI[loai],
          page: trang,
          pageSize: SO_DONG_TRANG,
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc: false,
    phuThuoc: [loai, trang],
  });
  const tong = ds.duLieu?.total ?? 0;
  const soTrang = Math.max(1, Math.ceil(tong / SO_DONG_TRANG));
  const daChon = new Set(chon.map((b) => b.id));

  const coBang = bangChon.length > 0;
  const moTaDich = coBang
    ? dienVao(t.dichBang, {
        so: bangChon.length,
        ds: bangChon
          .slice(0, 3)
          .map((d) => d.tenThietBi || d.bangDienTuId)
          .join(', '),
        them: bangChon.length > 3 ? '…' : '',
      })
    : dienVao(t.dichDiaBan, { ten: tenDiaBan });
  const khongCoDich = !coBang && diaBan.length === 0;

  const xemLai = async (b: DongBanTin): Promise<void> => {
    if (xem?.id === b.id) return datXem(null);
    const laMedia = b.loaiBanTin === '2' || b.loaiBanTin === '4';
    datXem({ id: b.id, tieuDe: b.tieuDe, kieu: laMedia ? b.loaiBanTin : '3', url: null });
    try {
      const url = laMedia
        ? (await api.linkTep(b.id)).url
        : ((await api.doanAmThanh(b.id)).find((x) => x.urlNgheThu)?.urlNgheThu ?? null);
      datXem({ id: b.id, tieuDe: b.tieuDe, kieu: laMedia ? b.loaiBanTin : '3', url: url || null });
    } catch {
      datXem({ id: b.id, tieuDe: b.tieuDe, kieu: laMedia ? b.loaiBanTin : '3', url: null });
    }
  };

  const phat = async (): Promise<void> => {
    if (chon.length === 0 || khongCoDich) return;
    datDangGui(true);
    datLoi(null);
    try {
      const kq = await api.phatKhanCap({
        banTinId: (chon[0] as DongBanTin).id,
        banTinIds: chon.map((b) => b.id),
        phamVi: coBang
          ? [...new Set(bangChon.map((d) => d.nguonId).filter((x): x is string => !!x))]
          : diaBan,
        bangDienTu: coBang ? bangChon.map((d) => d.bangDienTuId) : undefined,
        phanVungManHinh: phanVung,
        lyDo: dienVao(t.lyDoTuDong, { so: chon.length, dich: moTaDich }),
      });
      onXong(kq);
    } catch (e) {
      datLoi(dienVao(t.loi, { loi: loiThanhChuoi(e) }));
      datDangGui(false);
    }
  };

  const vungPhat = (b: DongBanTin): string =>
    viBangTin.vungPhat[(b.vungPhat ?? '').trim() || '0'] ?? b.vungPhat ?? '';

  return (
    <div className="man-che" role="presentation">
      <div
        className="hop-thoai tb-phat-kc bt-kc"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTieuDe}
        data-vung="phat-khan-cap-bang-tin"
      >
        <div className="tb-phat-kc__dau">
          <h2 id={idTieuDe}>{t.tieuDe}</h2>
          <button type="button" className="tb-phat-kc__dong" aria-label={t.dong} onClick={onDong}>
            ×
          </button>
        </div>

        <div className="bt-kc__tren">
          <label className="bt-kc__truong" htmlFor="bt-kc-vung">
            {t.chonVungPhat}
            <select
              id="bt-kc-vung"
              value={phanVung}
              onChange={(e) => datPhanVung(e.target.value === '1' ? '1' : '0')}
            >
              {(['0', '1'] as const).map((v) => (
                <option key={v} value={v}>
                  {viBangTin.phanVungManHinh[v]}
                </option>
              ))}
            </select>
          </label>
          <label className="bt-kc__truong bt-kc__truong--ngang" htmlFor="bt-kc-loai">
            {t.loaiBanTin}
            <select
              id="bt-kc-loai"
              value={loai}
              onChange={(e) => {
                datLoai(e.target.value as NhomLoaiBanTin);
                datTrang(1);
              }}
            >
              {DS_LOAI.map((v) => (
                <option key={v} value={v}>
                  {t.loaiNhan[v]}
                </option>
              ))}
            </select>
          </label>
          <span className="bt-kc__nut-phai">
            <button
              type="button"
              className="nut nut--nguy-hiem"
              data-hanh-dong="phat-khan-cap"
              disabled={chon.length === 0 || dangGui || khongCoDich}
              onClick={() => void phat()}
            >
              <BieuTuong ten="phat-song" co={14} />
              {dangGui ? t.dangGui : t.phatKhanCap}
            </button>
            <button
              type="button"
              className="nut"
              data-hanh-dong="xoa-tat-ca"
              disabled={chon.length === 0 || dangGui}
              onClick={() => datChon([])}
            >
              {t.xoaTatCa}
            </button>
          </span>
        </div>

        <p className="tb-phat-kc__dich" data-vung="dich-phat">
          {moTaDich}
        </p>
        {khongCoDich ? <p className="tb-phat-kc__loi">{t.chuaCoDiaBan}</p> : null}
        {loi ? (
          <p className="tb-phat-kc__loi" role="alert">
            {loi}
          </p>
        ) : null}

        <div className="bt-kc__hai-bang">
          <section className="bt-kc__ben" aria-label={t.khoBanTin}>
            <div className="tb-phat-kc__bang">
              <table className="bang" data-vung="bang-kho">
                <thead>
                  <tr>
                    <th className="bang__giua">{t.cot.stt}</th>
                    <th>{t.cot.tieuDe}</th>
                    <th className="bang__giua">{t.cot.thoiLuong}</th>
                    <th className="bang__giua">{t.cot.xemLai}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {ds.trangThai === 'dang-tai' ? (
                    <tr>
                      <td colSpan={5}>{t.dangTai}</td>
                    </tr>
                  ) : (ds.duLieu?.items ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={5}>{t.rong}</td>
                    </tr>
                  ) : (
                    (ds.duLieu?.items ?? []).map((b, i) => (
                      <tr key={b.id} data-ban-tin={b.banTinId}>
                        <td className="bang__giua">{(trang - 1) * SO_DONG_TRANG + i + 1}</td>
                        <td>{b.tieuDe}</td>
                        <td className="bang__giua">{b.thoiLuong ?? '—'}</td>
                        <td className="bang__giua">
                          <button
                            type="button"
                            className="tb-phat-kc__xem"
                            aria-label={dienVao(t.xemLaiNhan, { tieuDe: b.tieuDe })}
                            aria-pressed={xem?.id === b.id}
                            onClick={() => void xemLai(b)}
                          >
                            <BieuTuong ten="mat" co={18} />
                          </button>
                        </td>
                        <td className="bang__giua">
                          <button
                            type="button"
                            className="nut nut--chinh nut--nho"
                            data-hanh-dong="them-ban-tin"
                            aria-label={dienVao(t.themNhan, { tieuDe: b.tieuDe })}
                            disabled={daChon.has(b.id) || chon.length >= TOI_DA_CHON}
                            onClick={() => datChon((cu) => [...cu, b])}
                          >
                            {daChon.has(b.id) ? t.daThem : `+ ${t.them}`}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="bt-kc__chan">
              <span>{dienVao(t.tong, { so: tong })}</span>
              <span className="bt-kc__trang">
                <button
                  type="button"
                  className="nut nut--nho"
                  aria-label={t.trangTruoc}
                  disabled={trang <= 1}
                  onClick={() => datTrang((x) => x - 1)}
                >
                  ‹
                </button>
                <span>{dienVao(t.soTrang, { trang, tong: soTrang })}</span>
                <button
                  type="button"
                  className="nut nut--nho"
                  aria-label={t.trangSau}
                  disabled={trang >= soTrang}
                  onClick={() => datTrang((x) => x + 1)}
                >
                  ›
                </button>
              </span>
            </div>
          </section>

          <section className="bt-kc__ben" aria-label={t.daChon}>
            <div className="tb-phat-kc__bang">
              <table className="bang" data-vung="bang-da-chon">
                <thead>
                  <tr>
                    <th className="bang__giua">{t.cot.stt}</th>
                    <th>{t.cot.tieuDe}</th>
                    <th className="bang__giua">{t.cot.thoiLuong}</th>
                    <th>{t.cot.vungPhat}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {chon.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="bt-kc__trong">
                        {t.trong}
                      </td>
                    </tr>
                  ) : (
                    chon.map((b, i) => (
                      <tr key={b.id} data-da-chon={b.banTinId}>
                        <td className="bang__giua">{i + 1}</td>
                        <td>{b.tieuDe}</td>
                        <td className="bang__giua">{b.thoiLuong ?? '—'}</td>
                        <td>{vungPhat(b)}</td>
                        <td className="bang__giua">
                          <button
                            type="button"
                            className="nut nut--nho"
                            aria-label={dienVao(t.boNhan, { tieuDe: b.tieuDe })}
                            onClick={() => datChon((cu) => cu.filter((x) => x.id !== b.id))}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {xem ? (
          <div className="tb-phat-kc__nghe" data-vung="xem-lai">
            {xem.url ? (
              xem.kieu === '3' ? (
                <TrinhPhatAmThanh nhan={xem.tieuDe} url={xem.url} />
              ) : (
                <TrinhXemTep url={xem.url} kieu={kieuTep(xem.kieu, xem.url)} nhan={xem.tieuDe} />
              )
            ) : (
              <p>{dienVao(t.chuaXemDuoc, { tieuDe: xem.tieuDe })}</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
